/**
 * 通知模板列表页
 * 根据 notification/frontend-spec.md 规范定义
 * Tab 内展示，无页面标题
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import {
  Table,
  Button,
  Space,
  Select,
  Input,
  Tag,
  Switch,
  Modal,
  message,
  Row,
  Col,
} from 'antd';
import {
  EditOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  pageNotificationTemplates,
  updateNotificationTemplate,
} from '@/services/notification/notificationService';
import type { NotificationTemplateDTO, QueryNotificationTemplatePageRequest } from '@/types/notification/notificationTemplate';
import {
  NotificationType,
  NotificationChannel,
  NOTIFICATION_TYPE_OPTIONS,
  NOTIFICATION_CHANNEL_OPTIONS,
} from '@/constants/notification';
import NotificationTemplateForm from './components/NotificationTemplateForm';

const NotificationTemplateList: React.FC = () => {
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();

  // 列表数据
  const [dataSource, setDataSource] = useState<NotificationTemplateDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 搜索条件
  const [searchParams, setSearchParams] = useState<{
    templateCode?: string;
    templateName?: string;
    notificationType?: NotificationType;
    channel?: NotificationChannel;
    isEnabled?: boolean;
  }>({});

  // Drawer 状态
  const [formVisible, setFormVisible] = useState(false);
  const [editRecord, setEditRecord] = useState<NotificationTemplateDTO | null>(null);

  // 加载数据
  const fetchData = async () => {
    setLoading(true);
    try {
      const params: QueryNotificationTemplatePageRequest = {
        currentPage,
        pageSize,
        ...searchParams,
      };
      const response = await pageNotificationTemplates(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  // 搜索
  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  // 重置
  const handleReset = () => {
    setSearchParams({});
    setCurrentPage(1);
    fetchData();
  };

  // 搜索条件变化
  const handleSearchChange = (field: string, value: any) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 编辑
  const handleEdit = (record: NotificationTemplateDTO) => {
    setEditRecord(record);
    setFormVisible(true);
  };

  // 启用/禁用切换
  const handleToggleEnabled = async (record: NotificationTemplateDTO) => {
    const newEnabled = !record.isEnabled;

    const confirmMessage = newEnabled
      ? t('notification.template.list.confirm.enable')
      : t('notification.template.list.confirm.disable');

    Modal.confirm({
      title: newEnabled ? t('notification.template.list.actions.enable') : t('notification.template.list.actions.disable'),
      content: confirmMessage,
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await updateNotificationTemplate({
            templateCode: record.templateCode,
            isEnabled: newEnabled,
          });
          if (response.success) {
            message.success(newEnabled
              ? t('notification.template.list.message.enableSuccess')
              : t('notification.template.list.message.disableSuccess'));
            fetchData();
          } else {
            message.error(response.errorMessage || t('common.message.operationFailed'));
          }
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // 表格列定义
  const columns: ColumnsType<NotificationTemplateDTO> = [
    {
      title: t('notification.template.table.templateCode'),
      dataIndex: 'templateCode',
      key: 'templateCode',
      width: 200,
    },
    {
      title: t('notification.template.table.templateName'),
      dataIndex: 'templateName',
      key: 'templateName',
      width: 250,
    },
    {
      title: t('notification.template.table.notificationType'),
      dataIndex: 'notificationType',
      key: 'notificationType',
      width: 120,
      render: (type: NotificationType) => {
        const option = NOTIFICATION_TYPE_OPTIONS.find(opt => opt.value === type);
        return <Tag color={option?.color}>{t(option?.label || '')}</Tag>;
      },
    },
    {
      title: t('notification.template.table.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (channel: NotificationChannel) => {
        const option = NOTIFICATION_CHANNEL_OPTIONS.find(opt => opt.value === channel);
        return t(option?.label || '');
      },
    },
    {
      title: t('notification.template.table.isEnabled'),
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      render: (enabled: boolean, record: NotificationTemplateDTO) => (
        <Switch
          checked={enabled}
          onChange={() => handleToggleEnabled(record)}
        />
      ),
    },
    {
      title: t('notification.template.table.variables'),
      dataIndex: 'variables',
      key: 'variables',
      width: 200,
      render: (variables: string[]) => (
        <>
          {variables?.map(v => (
            <Tag key={v} color="blue">{v}</Tag>
          ))}
        </>
      ),
    },
    {
      title: t('notification.template.table.updateTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      sorter: true,
      render: (time: number) => dayjs(time).format('DD-MM-YYYY HH:mm:ss'),
    },
    {
      title: t('notification.template.table.actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        >
          {t('notification.template.list.actions.edit')}
        </Button>
      ),
    },
  ];

  return (
    <div className="notification-template-list" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 搜索区域 */}
      <div className="search-area">
        <Row gutter={[16, 16]}>
          <Col span={4}>
            <Input
              placeholder={t('notification.template.list.search.placeholder.templateCode')}
              value={searchParams.templateCode}
              onChange={(e) => handleSearchChange('templateCode', e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Input
              placeholder={t('notification.template.list.search.placeholder.templateName')}
              value={searchParams.templateName}
              onChange={(e) => handleSearchChange('templateName', e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('notification.template.list.search.placeholder.notificationType')}
              value={searchParams.notificationType}
              onChange={(value) => handleSearchChange('notificationType', value)}
              allowClear
              style={{ width: '100%' }}
              options={NOTIFICATION_TYPE_OPTIONS.map(opt => ({
                value: opt.value,
                label: t(opt.label),
              }))}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('notification.template.list.search.placeholder.channel')}
              value={searchParams.channel}
              onChange={(value) => handleSearchChange('channel', value)}
              allowClear
              style={{ width: '100%' }}
              options={NOTIFICATION_CHANNEL_OPTIONS.map(opt => ({
                value: opt.value,
                label: t(opt.label),
              }))}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('notification.template.list.search.placeholder.isEnabled')}
              value={searchParams.isEnabled}
              onChange={(value) => handleSearchChange('isEnabled', value)}
              allowClear
              style={{ width: '100%' }}
              options={[
                { value: true, label: t('common.status.enabled') },
                { value: false, label: t('common.status.disabled') },
              ]}
            />
          </Col>
          <Col span={4}>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                {t('common.search')}
              </Button>
              <Button onClick={handleReset}>
                {t('common.reset')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* 表格 */}
      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200, y: scrollY }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('common.pagination.total', { total }),
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </div>

      {/* 编辑 Drawer */}
      <NotificationTemplateForm
        visible={formVisible}
        record={editRecord}
        onClose={() => {
          setFormVisible(false);
          setEditRecord(null);
        }}
        onSuccess={() => {
          setFormVisible(false);
          setEditRecord(null);
          fetchData();
        }}
      />
    </div>
  );
};

export default NotificationTemplateList;
